import { useRef } from 'react';
import type { MeterFrame } from '@shared/protocol';
import { getPalette, useCanvas } from '../../lib/canvas';
import { assessBalance, assessCorrelation, assessMono, fmtSigned } from '../../lib/meterMath';
import type { LiveRefs } from '../../lib/useStreamLive';
import { Panel } from '../Panel';
import { LevelBadge } from '../Status';
import { groupOf } from './LoudnessPanel';

/** Horizontal indicator bar with a moving marker; zones are drawn left to right. */
function IndicatorBar({
  live,
  pick,
  min,
  max,
  zones,
  ticks,
  smoothing = 0.25,
  label,
}: {
  live: React.RefObject<LiveRefs>;
  pick: (f: MeterFrame) => number | null;
  min: number;
  max: number;
  zones: { to: number; color: 'ok' | 'warn' | 'over' }[];
  ticks: { v: number; label: string }[];
  smoothing?: number;
  label: string;
}) {
  const value = useRef(NaN);
  const ref = useCanvas((ctx, w, h) => {
    const p = getPalette();
    const f = live.current?.frame;
    const v = f ? pick(f) : null;
    if (v === null) value.current = NaN;
    else value.current = Number.isNaN(value.current) ? v : value.current + (v - value.current) * smoothing;
    const barH = 10;
    const xOf = (x: number) => ((Math.max(min, Math.min(max, x)) - min) / (max - min)) * (w - 2) + 1;
    ctx.clearRect(0, 0, w, h);
    let from = min;
    for (const z of zones) {
      ctx.fillStyle = p[z.color];
      ctx.globalAlpha = 0.35;
      ctx.fillRect(xOf(from), 6, xOf(z.to) - xOf(from), barH);
      from = z.to;
    }
    ctx.globalAlpha = 1;
    ctx.font = `11px ${p.font}`;
    ctx.fillStyle = p.scale;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (const t of ticks) {
      const x = Math.round(xOf(t.v)) + 0.5;
      ctx.fillRect(x - 0.5, 6 + barH, 1, 3);
      ctx.fillText(t.label, Math.min(w - 10, Math.max(10, x)), 6 + barH + 5);
    }
    if (!Number.isNaN(value.current)) {
      const x = xOf(value.current);
      ctx.fillStyle = p.hold;
      ctx.fillRect(Math.round(x) - 2, 2, 4, barH + 8);
    }
  });
  return <canvas ref={ref} className="indicator-canvas" role="img" aria-label={label} />;
}

export function StereoPanel({ live, frame, groupId }: { live: React.RefObject<LiveRefs>; frame: MeterFrame | null; groupId: string | null }) {
  const group = groupOf(frame, groupId);
  const st = group?.stereo ?? null;
  const corr = assessCorrelation(st?.correlation ?? null);
  const bal = assessBalance(st?.balance ?? null);
  const mono = assessMono(st?.monoDelta ?? null);
  const isMono = !!group && group.channels.length < 2;

  return (
    <Panel title={`Phase and stereo image${group && frame && frame.groups.length > 1 ? `: ${group.name}` : ''}`}>
      {isMono ? (
        <p className="muted">This channel group has a single audio channel.</p>
      ) : (
        <div className="stereo-rows">
          <div className="stereo-row">
            <div className="stereo-head">
              <span className="stereo-name">Phase correlation</span>
              <span className="num">{st?.correlation === null || !st ? '--' : fmtSigned(st.correlation, 2)}</span>
              <LevelBadge level={corr.level}>{corr.text}</LevelBadge>
            </div>
            <IndicatorBar
              live={live}
              label="Phase correlation from -1 to +1"
              pick={(f) => groupOf(f, groupId)?.stereo?.correlation ?? null}
              min={-1}
              max={1}
              zones={[
                { to: -0.3, color: 'over' },
                { to: 0, color: 'warn' },
                { to: 1, color: 'ok' },
              ]}
              ticks={[
                { v: -1, label: '-1' },
                { v: -0.5, label: '-0.5' },
                { v: 0, label: '0' },
                { v: 0.5, label: '+0.5' },
                { v: 1, label: '+1' },
              ]}
            />
          </div>
          <div className="stereo-row">
            <div className="stereo-head">
              <span className="stereo-name">Balance (R minus L)</span>
              <span className="num">{st?.balance === null || !st ? '--' : `${fmtSigned(st.balance)} dB`}</span>
              <LevelBadge level={bal.level}>{bal.text}</LevelBadge>
            </div>
            <IndicatorBar
              live={live}
              label="Left/right balance in dB"
              pick={(f) => groupOf(f, groupId)?.stereo?.balance ?? null}
              min={-12}
              max={12}
              zones={[
                { to: -3, color: 'warn' },
                { to: 3, color: 'ok' },
                { to: 12, color: 'warn' },
              ]}
              ticks={[
                { v: -12, label: 'L 12' },
                { v: -6, label: '6' },
                { v: 0, label: '0' },
                { v: 6, label: '6' },
                { v: 12, label: 'R 12' },
              ]}
            />
          </div>
          <div className="stereo-row">
            <div className="stereo-head">
              <span className="stereo-name">Mono compatibility</span>
              <span className="num">{st?.monoDelta === null || !st ? '--' : `${fmtSigned(st.monoDelta)} dB`}</span>
              <LevelBadge level={mono.level}>{mono.text}</LevelBadge>
            </div>
            <IndicatorBar
              live={live}
              label="Mono sum loudness relative to stereo, dB"
              pick={(f) => groupOf(f, groupId)?.stereo?.monoDelta ?? null}
              min={-20}
              max={1}
              zones={[
                { to: -6, color: 'over' },
                { to: -3.5, color: 'warn' },
                { to: 1, color: 'ok' },
              ]}
              ticks={[
                { v: -20, label: '-20' },
                { v: -12, label: '-12' },
                { v: -6, label: '-6' },
                { v: -3, label: '-3' },
                { v: 0, label: '0 dB' },
              ]}
            />
            <p className="muted small">Short-term loudness of the mono sum compared with stereo. 0 dB means nothing is lost in mono; about -3 dB is normal for wide stereo.</p>
          </div>
        </div>
      )}
    </Panel>
  );
}
